import { PassengerType } from "@b2bportal/core-types";
import type { RewardsPrice } from "@b2bportal/core-utilities";
import { getPassengerType } from "@b2bportal/core-utilities";
import {
  FlightSelectors,
  FlightPassengerSelectors,
  ParentState,
  getParentState,
} from "@hopper-b2b/checkout";
import type {
  ChoosePaymentProperties,
  ChooseTravelerProperties,
  Prices,
  TripCategory,
} from "@hopper-b2b/types";
import { ZERO_FIAT_AMOUNT } from "@hsbc/shared";
import type { FlightMachineContext } from "@hsbc/types";
import dayjs from "dayjs";
import { sum } from "lodash-es";
import type { State } from "xstate";

type CheckoutState = State<FlightMachineContext>;
type CheckoutStateWithoutValue = Pick<CheckoutState, "context">;
type CheckoutStateWithAndWithoutValue =
  | CheckoutState
  | CheckoutStateWithoutValue;

export const getProductTotalPrice = (
  state: CheckoutStateWithAndWithoutValue
): Prices => {
  return (
    state.context.cartQuote?.quoteBreakdown?.productTotal ?? {
      fiat: ZERO_FIAT_AMOUNT,
      accountSpecific: {},
    }
  );
};

export const getProductTotalPriceInRewards = (
  state: CheckoutStateWithAndWithoutValue
): RewardsPrice | null => {
  const { rewards, accountSpecific } = getProductTotalPrice(state);
  const rewardsValues: Array<RewardsPrice> = Object.values(
    rewards || accountSpecific
  );

  return rewardsValues.length ? rewardsValues[0] : null;
};

export const getAfterQuoteTotalPrice = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const { accountSpecific, fiat } = getProductTotalPrice(state);
  const rewardsValues: Array<RewardsPrice> = Object.values(accountSpecific);

  return {
    fiat,
    rewards: rewardsValues?.[0] ?? { value: 0, currency: "points" },
  };
};

export const getTripCategory = (state: CheckoutStateWithAndWithoutValue) =>
  state.context.flightSearch.tripCategory;

export const getFlightSearchDestination = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.destination;

export const getFlightSearchOrigin = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.origin;

export const getOriginCountryCode = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.originCountryCode;

export const getDestinationCountryCode = ({
  context,
}: CheckoutStateWithAndWithoutValue) =>
  context.flightSearch.destinationCountryCode;

export const getFlightSearchDepartureDate = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.departureDate;

export const getFlightSearchReturnDate = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.returnDate;

export const getSelectedTripDetails = (
  state: CheckoutStateWithAndWithoutValue
) => state.context.flightShop.tripDetails;

export const getPricing = (state: CheckoutStateWithAndWithoutValue) => {
  const parentState = getParentState((state as CheckoutState).value);

  const shouldUseQuote = !(
    parentState === ParentState.contactInformation ||
    parentState === ParentState.passengerInformation
  );

  const tripPricing = state.context.common.tripPricing;

  if (!shouldUseQuote) return tripPricing;

  const quoteTripPricing =
    FlightSelectors.getQuoteBreakdownFlightPricing(state);
  const validPriceQuote =
    (quoteTripPricing?.pricingByPassenger?.length || 0) > 0;
  return validPriceQuote ? quoteTripPricing : tripPricing;
};

export const getChooseTravelerProperties = ({
  context,
}: CheckoutStateWithAndWithoutValue): ChooseTravelerProperties => {
  const tripCategory = getTripCategory({ context }) as TripCategory;
  const destination = getFlightSearchDestination({ context });
  const origin = getFlightSearchOrigin({ context });
  const originCountryCode = getOriginCountryCode({ context });
  const destinationCountryCode = getDestinationCountryCode({ context });
  const departureDate = getFlightSearchDepartureDate({ context });
  const returnDate = getFlightSearchReturnDate({ context });
  const formattedDepartureDate = dayjs(departureDate).format("YYYY-MM-DD");
  const formattedReturnDate =
    returnDate && dayjs(returnDate).format("YYYY-MM-DD");

  return {
    departure_date: formattedDepartureDate,
    destination_country_code: destinationCountryCode,
    destination: `${destination?.id.code.regionType}/${destination?.id.code.code}`,
    origin_country_code: originCountryCode,
    origin: `${origin?.id.code.regionType}/${origin?.id.code.code}`,
    return_date: formattedReturnDate ? formattedReturnDate : undefined,
    trip_type: tripCategory,
  };
};

export const getChoosePaymentProperties = ({
  context,
}: CheckoutStateWithAndWithoutValue): ChoosePaymentProperties => {
  const chooseTravelerProperties = getChooseTravelerProperties({ context });
  const tripPricing = getPricing({ context });
  const departureDate = getFlightSearchDepartureDate({ context });
  const totalSelectedPassengers =
    FlightPassengerSelectors.getAllSelectedPassengerIdsParent({
      context,
    }).length;
  const selectedInfants =
    FlightPassengerSelectors.getSelectedLapInfantIdsParent({
      context,
    }).length;
  const userPassengers = FlightPassengerSelectors.getUserPassengersParent({
    context,
  });
  const seatedPassengerIds =
    FlightPassengerSelectors.getSelectedPassengerIdsParent({
      context,
    });
  const selectedChildren = userPassengers.filter((pax) => {
    if (!seatedPassengerIds.includes(pax.id)) return false;
    return (
      getPassengerType({
        dateOfBirth: pax.dateOfBirth,
        tripDate: departureDate ?? undefined,
      }) === PassengerType.Child
    );
  }).length;
  const totalStops = sum(
    getSelectedTripDetails({ context }).slices.map((s) => s.stops)
  );

  return {
    ...chooseTravelerProperties,
    adult_price_total_usd: 0,
    base_total_usd: 0,
    seat_infant_price_total_usd: 0,
    price_per_pax_usd: 0,
    subtotal_usd: 0,
    total_price_usd: 0,
    charged_PQ_currency:
      tripPricing?.totalPricing.total.fiat.currencyCode || "USD",
    total_price: tripPricing?.totalPricing.total.fiat.value || 0,
    pax_total: totalSelectedPassengers,
    selected_pax_children: selectedChildren,
    selected_pax_lap_infants: selectedInfants,
    selected_pax_total: totalSelectedPassengers,
    total_stops: totalStops,
  };
};
